import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
    collection, onSnapshot, updateDoc, doc, setDoc, serverTimestamp,
    getDoc, deleteDoc, query, orderBy, where, limit, Timestamp, getDocs
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import 'react-datepicker/dist/react-datepicker.css';
import { logUserAction, logEntityChanges } from '../utils/logger';
import {
    uploadAttachment,
    deleteAttachment,
    subscribeToAttachments,
    checkAttachmentExists
} from '../utils/attachmentsUtils';
import { formatDate } from '../utils/dateHelpers';
import { collectionPath } from '../utils/firestoreCollections';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { useFirestoreListener } from '../utils/listenerManager';
import AssociationBadges from '../components/common/AssociationBadges';
import { resolveAssociations, createAssociatedNote, deleteNote } from '../utils/associationUtils';
import TaskModal from '../components/forms/TaskModal';

function isPastOrToday(dateStrOrObj) {
    let date;
    if (!dateStrOrObj) return false;
    if (typeof dateStrOrObj === 'string') {
        date = new Date(dateStrOrObj + 'T00:00:00');
    } else if (dateStrOrObj.seconds) {
        date = new Date(dateStrOrObj.seconds * 1000);
    } else {
        date = new Date(dateStrOrObj);
    }
    const today = new Date();
    today.setHours(0,0,0,0);
    date.setHours(0,0,0,0);
    return date <= today;
}

const TaskDetailPage = () => {
    const { id } = useParams();
    const { currentUser } = useAuth();
    const { can } = usePermissions(currentUser);
    const [task, setTask] = useState(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [taskNotes, setTaskNotes] = useState([]);
    const [users, setUsers] = useState([]);
    const [notesSearch, setNotesSearch] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const editParam = params.get('edit'); // Check for edit=true query param

    // TaskModal state
    const [showEditModal, setShowEditModal] = useState(false);

    // tabs: 'notes' | 'activity'
    const [activeTab, setActiveTab] = useState('notes');

    // Activity Log search state
    const [activitySearch, setActivitySearch] = useState('');

    // activity logs for this task
    const [logs, setLogs] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const fileInputRef = useRef(null);

    // Smart Tasks state
    const [actionItems, setActionItems] = useState([]);
    const [showCompletionDialog, setShowCompletionDialog] = useState(false);
    const [completionNote, setCompletionNote] = useState('');
    const [showActionItemNoteDialog, setShowActionItemNoteDialog] = useState(false);
    const [actionItemNote, setActionItemNote] = useState('');
    const [pendingActionItemIndex, setPendingActionItemIndex] = useState(null);

    // Association data for TaskModal
    const [customers, setCustomers] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [orders] = useState([]); // Orders loaded via lazy search in TaskModal
    const [contacts, setContacts] = useState([]);
    const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false);

    // Track whether we've processed the auto-open logic
    const hasProcessedEditParam = useRef(false);
    
    useEffect(() => {
        const ref = doc(db, collectionPath('tasks', id));
        const unsub = onSnapshot(ref, snap => {
            if (snap.exists()) {
                const taskData = { id: snap.id, ...snap.data() };
                // Preserve resolved association names (contactName, customerName, etc.) from
                // previous state if the IDs haven't changed. resolveAssociations derives these
                // from Firestore lookups and does not store them on the task document, so each
                // snapshot would otherwise wipe them out causing the "shows then disappears" bug.
                setTask(prev => {
                    if (prev?.associations && taskData.associations) {
                        const prevA = prev.associations;
                        const newA = taskData.associations;
                        const merged = { ...newA };
                        if (prevA.contactId === newA.contactId && prevA.contactName) merged.contactName = prevA.contactName;
                        if (prevA.customerId === newA.customerId && prevA.customerName) merged.customerName = prevA.customerName;
                        if (prevA.opportunityId === newA.opportunityId && prevA.opportunityNumber) {
                            merged.opportunityNumber = prevA.opportunityNumber;
                            merged.opportunityNumberLabel = prevA.opportunityNumberLabel;
                        }
                        if (prevA.orderId === newA.orderId && prevA.orderNumber) merged.orderNumber = prevA.orderNumber;
                        taskData.associations = merged;
                    }
                    return taskData;
                });
                // Initialize action items from task data
                setActionItems(taskData.actionItems || []);
                
                // Auto-open edit modal if edit=true in URL (only once per session)
                if (editParam === 'true' && !hasProcessedEditParam.current) {
                    hasProcessedEditParam.current = true;
                    setShowEditModal(true);
                    setHasAutoOpenedModal(true);
                    // Clear the query param after setting edit mode
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                }
            }
        });
        return unsub;
    }, [id, editParam]); // Only re-subscribe when id or editParam changes

    // Listen for notes in the unified notes collection (using associations.taskId)
    useEffect(() => {
        if (!id) return;
        const notesQuery = query(
            collection(db, collectionPath('notes')),
            where('associations.taskId', '==', id),
            orderBy('createdAt', 'desc'),
            limit(100)
        );
        const unsub = onSnapshot(notesQuery, (snap) => {
            setTaskNotes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, [id]);

    // When fetching users:
    useEffect(() => {
        if (!currentUser) return;
        // Load all users if user can view all tasks OR can assign tasks to any user
        // (e.g. Customer Support role needs the full list for the TaskModal assignee dropdown)
        if (can(FEATURE_PERMISSIONS.TASK_VIEW_ALL) || can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY)) {
            const unsub = onSnapshot(collection(db, collectionPath('users')), (snap) => {
                setUsers(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
            });
            return unsub;
        } else {
            getDoc(doc(db, collectionPath('users', currentUser.uid))).then(snap => {
                if (snap.exists()) setUsers([{ uid: snap.id, ...snap.data() }]);
                else setUsers([]);
            });
        }
    }, [currentUser, can]);

    // Load customers when in edit mode
    const loadCustomers = async () => {
        const snap = await getDocs(collection(db, collectionPath('customers')));
        setCustomers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    // Load opportunities when in edit mode
    const loadOpportunities = async () => {
        const snap = await getDocs(collection(db, collectionPath('opportunities')));
        setOpportunities(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    // Load contacts when in edit mode - contacts are now in standalone collection
    // Document ID = ApparelMagic contact ID (contactId field)
    const loadContacts = async () => {
        console.log('Loading contacts from contacts collection...');
        const contactsSnap = await getDocs(query(
            collection(db, collectionPath('contacts')),
            limit(5000)
        ));
        
        const allContacts = contactsSnap.docs.map(doc => ({
            id: doc.id,  // This is the contactId (ApparelMagic ID)
            ...doc.data()
        }));
        
        console.log('Loaded contacts:', allContacts.length);
        setContacts(allContacts);
    };

    // Resolve associations when task data changes.
    // Prefer task.associations.* (set by TaskModal save) over root-level fields
    // (root-level fields are older compatibility fields and may be absent).
    const lastResolvedRef = useRef(null);
    useEffect(() => {
        if (!task) return;
        const assoc = task.associations || {};
        // Build a stable key from all possible ID sources to detect real changes
        const idKey = [
            assoc.customerId || task.customerId || '',
            assoc.opportunityId || task.opportunityId || '',
            assoc.orderId || task.orderId || '',
            assoc.contactId || task.contactId || ''
        ].join('|');
        // Skip if we already resolved this exact combination
        if (lastResolvedRef.current === idKey) return;
        lastResolvedRef.current = idKey;
        const resolve = async () => {
            const resolved = await resolveAssociations({
                customerId: assoc.customerId || task.customerId,
                opportunityId: assoc.opportunityId || task.opportunityId,
                orderId: assoc.orderId || task.orderId,
                contactId: assoc.contactId || task.contactId
            });
            setTask(prev => ({ ...prev, associations: resolved }));
        };
        resolve();
    }, [task?.customerId, task?.opportunityId, task?.orderId, task?.contactId,
        task?.associations?.customerId, task?.associations?.opportunityId,
        task?.associations?.orderId, task?.associations?.contactId]);

    // Memoize the query to prevent recreating it on every render
    const logsQuery = useMemo(() => {
        if (!id) return null;
        return query(
            collection(db, collectionPath('logsTask')),
            where('taskId', '==', id),
            orderBy('timestamp', 'desc'),
            limit(50)
        );
    }, [id]);

    // Use optimized listener with proper composite index (taskId + timestamp desc)
    // Composite index required: logsTask collection with (taskId ASC, timestamp DESC)
    useFirestoreListener(
        `task-logs-${id}`,
        logsQuery,
        (snapshot) => {
            const fetchedLogs = snapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            }));
            setLogs(fetchedLogs);
        },
        [logsQuery],  // Include query as dependency to ensure proper updates
        {
            onError: (err) => {
                console.error('Error loading task logs:', err);
                // If composite index doesn't exist yet, show helpful error
                if (err.code === 'failed-precondition') {
                    console.warn('Composite index not ready. Run: firebase deploy --only firestore:indexes');
                }
            }
        }
    );

    // Subscribe to attachments for this task
    useEffect(() => {
        if (!id) return;
        const unsub = subscribeToAttachments(id, 'task', setAttachments);
        return () => unsub();
    }, [id]);

    const getRepName = (uid) => {
        const user = users.find(u => uid === u.uid);
        return (
            user?.displayName ||
            user?.name ||
            `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
            user?.email ||
            uid ||
            '-'
        );
    };

    const saveNote = async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            // Use unified notes collection with proper associations
            await createAssociatedNote(
                {
                    text: note.trim(),
                    sourceEntityType: 'task',
                    sourceEntityId: id,
                    attachments: []
                },
                currentUser,
                {
                    taskId: id,
                    // Include customer/opportunity associations from task if available
                    customerId: task?.associations?.customerId || null,
                    customerName: task?.associations?.customerName || null,
                    opportunityId: task?.associations?.opportunityId || null,
                    opportunityNumber: task?.associations?.opportunityNumber || null
                }
            );

            // Update task's lastUpdated timestamp
            await updateDoc(doc(db, collectionPath('tasks'), id), {
                updatedAt: serverTimestamp()
            });

            // Log the note addition
            try {
                await logUserAction({
                    userId: currentUser?.uid || currentUser?.id || 'unknown',
                    userEmail: currentUser?.email || null,
                    userName: currentUser?.displayName || currentUser?.name || null,
                    action: 'Added Note',
                    details: `Note: "${note.trim()}"`,
                    entityType: 'Task',
                    entityId: id,
                    taskId: id,
                    customerId: task?.customerId || null
                });
            } catch (logErr) {
                console.error('Failed to write log for note', logErr);
            }

            setNote('');
        } catch (err) {
            console.error('Failed to save note', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTask = async () => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                // Delete the task document
                await deleteDoc(doc(db, collectionPath('tasks', id)));
                
                // Delete all associated notes from unified collection
                const notesQuery = query(
                    collection(db, collectionPath('notes')),
                    where('associations.taskId', '==', id)
                );
                const notesSnapshot = await getDocs(notesQuery);
                const deletePromises = notesSnapshot.docs.map(noteDoc =>
                    deleteDoc(doc(db, collectionPath('notes'), noteDoc.id))
                );
                await Promise.all(deletePromises);
                
                // log deletion
                try {
                    await logUserAction({
                        userId: currentUser?.uid || currentUser?.id || 'unknown',
                        userEmail: currentUser?.email || null,
                        userName: currentUser?.displayName || currentUser?.name || null,
                        action: 'Deleted Task',
                        details: `Task deleted: ${task?.title || id}`,
                        entityType: 'Task',
                        entityId: id,
                        taskId: id,
                        customerId: task?.customerId || null
                    });
                } catch (logErr) {
                    console.error('Failed to log task deletion', logErr);
                }
                navigate('/tasks');
            } catch (err) {
                console.error('Failed to delete task', err);
            }
        }
    };

    if (!task) return <div className="p-6">Loading...</div>;

    // --- Calculate overdue status above the return ---
    let dueDateDisplay = '-';
    if (task.dueDate) {
        let due;
        if (typeof task.dueDate === 'string') {
            due = new Date(task.dueDate);
        } else if (typeof task.dueDate === 'object' && task.dueDate.seconds) {
            due = new Date(task.dueDate.seconds * 1000);
        } else {
            due = new Date(task.dueDate);
        }
        dueDateDisplay = due ? formatDate(due, currentUser?.dateFormat) : '-';
    }

    // Notes now come from unified notes collection (using associations.taskId)
    let notesArr = taskNotes;

    const canViewAll = can(FEATURE_PERMISSIONS.TASK_VIEW_ALL);
    const canSelfUpdateStatus = !canViewAll && currentUser?.uid && task?.userId === currentUser.uid;

    const handleQuickStatusChange = async (newStatus) => {
      try {
        const old = task || {};
        await updateDoc(doc(db, collectionPath('tasks', id)), {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
        try {
          await logEntityChanges({
            oldObj: old,
            newObj: { ...(old || {}), status: newStatus },
            fields: ['status'],
            entityType: 'Task',
            entityId: id,
            user: {
              uid: currentUser?.uid,
              email: currentUser?.email,
              displayName: currentUser?.displayName,
              firstName: currentUser?.firstName,
              lastName: currentUser?.lastName,
              id: currentUser?.id,
              collectionName: collectionPath('logsTask')
            },
            action: 'Updated Task Status',
          });
        } catch (logErr) {
          console.error('Failed to log task status change', logErr);
        }
      } catch (err) {
        console.error('Failed to update status', err);
      }
    };

    // Smart Tasks: Toggle action item checkbox
    const handleToggleActionItem = (index) => {
        const wasCompleted = actionItems[index].completed;
        
        // If checking OFF (uncompleting), just update immediately
        if (wasCompleted) {
            const updatedItems = [...actionItems];
            updatedItems[index].completed = false;
            setActionItems(updatedItems);
            
            updateDoc(doc(db, collectionPath('tasks', id)), {
                actionItems: updatedItems,
                updatedAt: serverTimestamp()
            }).catch(err => console.error('Failed to update action items', err));
            logUserAction({
                action: 'Action Item Uncompleted',
                details: `Uncompleted action item: "${actionItems[index]?.text || ''}"`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
            }).catch(e => console.warn('Failed to log action item toggle:', e));
            return;
        }
        
        // If checking ON (completing), prompt for note
        setPendingActionItemIndex(index);
        setShowActionItemNoteDialog(true);
    };

    // Smart Tasks: Save action item with optional note
    const handleSaveActionItemCompletion = async (skipNote = false) => {
        if (pendingActionItemIndex === null) return;
        
        const updatedItems = [...actionItems];
        updatedItems[pendingActionItemIndex].completed = true;
        setActionItems(updatedItems);

        try {
            await updateDoc(doc(db, collectionPath('tasks', id)), {
                actionItems: updatedItems,
                updatedAt: serverTimestamp()
            });

            // Log action item completion
            logUserAction({
                action: 'Action Item Completed',
                details: `Completed action item: "${updatedItems[pendingActionItemIndex].text}"${(!skipNote && actionItemNote.trim()) ? ` - Note: ${actionItemNote.trim()}` : ''}`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
            }).catch(e => console.warn('Failed to log action item completion:', e));

            // Save note if provided
            if (!skipNote && actionItemNote.trim()) {
                // Use unified notes collection with proper associations
                await createAssociatedNote(
                    {
                        text: `✓ Action Item Completed: "${updatedItems[pendingActionItemIndex].text}" - ${actionItemNote.trim()}`,
                        sourceEntityType: 'task',
                        sourceEntityId: id,
                        attachments: [],
                        actionItemIndex: pendingActionItemIndex
                    },
                    currentUser,
                    {
                        taskId: id,
                        customerId: task?.associations?.customerId || null,
                        customerName: task?.associations?.customerName || null,
                        opportunityId: task?.associations?.opportunityId || null,
                        opportunityNumber: task?.associations?.opportunityNumber || null
                    }
                );
            }

            // Check if all items are now completed
            const allCompleted = updatedItems.every(item => item.completed);
            if (allCompleted && task.status?.toLowerCase() !== 'completed') {
                setShowCompletionDialog(true);
            }
        } catch (err) {
            console.error('Failed to update action items', err);
        } finally {
            setShowActionItemNoteDialog(false);
            setActionItemNote('');
            setPendingActionItemIndex(null);
        }
    };

    // Smart Tasks: Add new action item
    const handleAddActionItem = async (text) => {
        if (!text.trim()) return;
        
        const newItem = {
            text: text.trim(),
            completed: false,
            createdAt: new Date().toISOString(),
            createdBy: currentUser?.uid || currentUser?.id
        };

        const updatedItems = [...actionItems, newItem];
        setActionItems(updatedItems);

        try {
            await updateDoc(doc(db, collectionPath('tasks', id)), {
                actionItems: updatedItems,
                updatedAt: serverTimestamp()
            });
            logUserAction({
                action: 'Action Item Added',
                details: `Added action item: "${text.trim()}"`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
            }).catch(e => console.warn('Failed to log action item add:', e));
        } catch (err) {
            console.error('Failed to add action item', err);
        }
    };

    // Smart Tasks: Delete action item
    const handleDeleteActionItem = async (index) => {
        const deletedItem = actionItems[index];
        const updatedItems = actionItems.filter((_, i) => i !== index);
        setActionItems(updatedItems);

        try {
            await updateDoc(doc(db, collectionPath('tasks', id)), {
                actionItems: updatedItems,
                updatedAt: serverTimestamp()
            });
            logUserAction({
                action: 'Action Item Deleted',
                details: `Deleted action item: "${deletedItem?.text || ''}"`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
            }).catch(e => console.warn('Failed to log action item delete:', e));
        } catch (err) {
            console.error('Failed to delete action item', err);
        }
    };

    // Smart Tasks: Complete task with note
    const handleCompleteTask = async () => {
        if (!completionNote.trim()) {
            alert('Please enter a completion note.');
            return;
        }

        setSaving(true);
        try {
            const old = task || {};
            
            // Update task status to completed
            await updateDoc(doc(db, collectionPath('tasks', id)), {
                status: 'Completed',
                updatedAt: serverTimestamp()
            });

            // Save completion note using unified notes collection
            await createAssociatedNote(
                {
                    text: `✅ Task Completed: ${completionNote.trim()}`,
                    sourceEntityType: 'task',
                    sourceEntityId: id,
                    attachments: []
                },
                currentUser,
                {
                    taskId: id,
                    customerId: task?.associations?.customerId || null,
                    customerName: task?.associations?.customerName || null,
                    opportunityId: task?.associations?.opportunityId || null,
                    opportunityNumber: task?.associations?.opportunityNumber || null
                }
            );

            // Log the completion
            await logEntityChanges({
                oldObj: old,
                newObj: { ...(old || {}), status: 'Completed' },
                fields: ['status'],
                entityType: 'Task',
                entityId: id,
                user: {
                    uid: currentUser?.uid,
                    email: currentUser?.email,
                    displayName: currentUser?.displayName,
                    firstName: currentUser?.firstName,
                    lastName: currentUser?.lastName,
                    id: currentUser?.id,
                    collectionName: collectionPath('logsTask')
                },
                action: 'Completed Task',
            });

            setShowCompletionDialog(false);
            setCompletionNote('');
        } catch (err) {
            console.error('Failed to complete task', err);
        } finally {
            setSaving(false);
        }
    };

    // Handle file upload for attachments
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setUploadError('');
        setUploading(true);

        try {
            const willOverwrite = await checkAttachmentExists({
                fileName: file.name,
                entityId: id,
                entityType: 'task'
            });

            if (willOverwrite) {
                const confirm = window.confirm(
                    `A file named "${file.name}" already exists. Uploading will overwrite the existing file. Do you want to continue?`
                );
                if (!confirm) {
                    setUploading(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }
            }

            await uploadAttachment({
                file,
                entityId: id,
                entityType: 'task',
                currentUser
            });

            // Log the upload action
            await logUserAction({
                action: 'Uploaded Attachment',
                details: `File "${file.name}" uploaded.`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
                customerId: task?.customerId || null,
                userEmail: currentUser?.email
            }, 'logsTask');
        } catch (err) {
            setUploadError(err.message || 'Upload failed.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Handle delete attachment
    const handleDeleteAttachment = async (attachment) => {
        if (!window.confirm('Delete this file?')) return;
        try {
            await deleteAttachment(attachment);

            // Log the delete action
            await logUserAction({
                action: 'Deleted Attachment',
                details: `File "${attachment.name}" deleted.`,
                entityType: 'Task',
                entityId: id,
                taskId: id,
                customerId: task?.customerId || null,
                userEmail: currentUser?.email
            }, 'logsTask');
        } catch (err) {
            setUploadError('Failed to delete attachment');
        }
    };

    return (
        <main className="bg-gray-50 p-4 md:p-6 lg:p-8 max-w-[1920px] mx-auto">
            <button
                className="mb-6 text-indigo-600 hover:underline w-fit flex items-center gap-1"
                onClick={() => navigate('/tasks')}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Tasks
            </button>

            {/* Task Header Card */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 lg:p-10 mb-6">
                {/* Title and Task Number */}
                <div className="mb-4">
                    <h1 className="text-3xl font-bold text-gray-900">{task.title}</h1>
                    {task.taskNumberLabel && (
                        <span className="inline-block mt-2 text-sm text-gray-500 font-mono bg-gray-100 px-3 py-1 rounded">
                            Task #{task.taskNumberLabel}
                        </span>
                    )}
                </div>

                {/* Metadata Grid - Horizontal Layout */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-6 pb-6 border-b">
                    {/* Status */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
                        {canViewAll ? (
                            <span className={
                                `inline-block px-3 py-1 rounded-full text-sm font-semibold 
                                ${task.status === 'Closed' ? 'bg-gray-200 text-gray-700'
                                  : task.status === 'Completed' ? 'bg-green-100 text-green-700'
                                  : 'bg-indigo-100 text-indigo-700'}`
                            }>
                                {task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
                            </span>
                        ) : (
                            task.status === 'Closed' || !canSelfUpdateStatus ? (
                                <span className={
                                    `inline-block px-3 py-1 rounded-full text-sm font-semibold 
                                    ${task.status === 'Closed' ? 'bg-gray-200 text-gray-700'
                                      : task.status === 'Completed' ? 'bg-green-100 text-green-700'
                                      : 'bg-indigo-100 text-indigo-700'}`
                                }>
                                    {task.status?.charAt(0).toUpperCase() + task.status?.slice(1)}
                                </span>
                            ) : (
                                <select
                                    className="w-full border rounded px-2 py-1 text-sm"
                                    value={task.status}
                                    onChange={e => handleQuickStatusChange(e.target.value)}
                                >
                                    <option value="Open">Open</option>
                                    <option value="Completed">Completed</option>
                                </select>
                            )
                        )}
                    </div>

                    {/* Assigned To */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Assigned To</label>
                        <div className="text-sm font-medium text-gray-900">{getRepName(task.uid)}</div>
                    </div>

                    {/* Created At */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Created</label>
                        <div className="text-sm font-medium text-gray-900">
                            {task.createdAt && task.createdAt.seconds
                                ? formatDate(new Date(task.createdAt.seconds * 1000), currentUser?.dateFormat)
                                : '-'}
                        </div>
                    </div>

                    {/* Due Date */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Due Date</label>
                        <div className={`text-sm font-medium ${isPastOrToday(task.dueDate) && task.status !== 'Completed' ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                            {dueDateDisplay}
                        </div>
                    </div>

                    {/* Priority */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Priority</label>
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                            task.priority === 'High' 
                                ? 'bg-red-100 text-red-800'
                                : task.priority === 'Low'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-yellow-100 text-yellow-800'
                        }`}>
                            {task.priority || 'Medium'}
                        </span>
                    </div>

                    {/* Last Updated */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Last Updated</label>
                        <div className="text-sm font-medium text-gray-900">
                            {task.updatedAt && task.updatedAt.seconds
                                ? formatDate(new Date(task.updatedAt.seconds * 1000), currentUser?.dateFormat)
                                : '-'}
                        </div>
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 rounded p-3">
                        {task.description || 'No description provided.'}
                    </div>
                </div>

                {/* Associations */}
                {task.associations && Object.keys(task.associations).some(key => task.associations[key]) && (
                    <div className="mt-4 pt-4 border-t">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Associated Entities</label>
                        <AssociationBadges associations={task.associations} />
                    </div>
                )}

                {/* Action Buttons */}
                {can(FEATURE_PERMISSIONS.TASK_EDIT) && (canViewAll || task.uid === currentUser?.uid) && (
                    <div className="mt-6 flex gap-3 pt-6 border-t">
                        <button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                            onClick={() => setShowEditModal(true)}
                        >
                            Edit Task
                        </button>
                        {can(FEATURE_PERMISSIONS.TASK_DELETE) && (canViewAll || task.uid === currentUser?.uid) && (
                            <button
                                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                                onClick={handleDeleteTask}
                            >
                                Delete Task
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Smart Tasks: Action Items Section */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 lg:p-10 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Action Items
                    </h2>
                    <span className="text-sm text-gray-500">
                        {actionItems.filter(item => item.completed).length} of {actionItems.length} completed
                    </span>
                </div>

                {/* Action Items List */}
                <div className="space-y-2 mb-4">
                    {actionItems.length === 0 ? (
                        <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
                            No action items yet. Add one below to get started!
                        </div>
                    ) : (
                        actionItems.map((item, index) => (
                            <div
                                key={index}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                    item.completed
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-white border-gray-200 hover:border-indigo-300'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={item.completed}
                                    onChange={() => handleToggleActionItem(index)}
                                    className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                                    disabled={!can(FEATURE_PERMISSIONS.TASK_EDIT)}
                                />
                                <span
                                    className={`flex-1 ${
                                        item.completed
                                            ? 'line-through text-gray-500'
                                            : 'text-gray-900'
                                    }`}
                                >
                                    {item.text}
                                </span>
                                {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                                <button
                                    onClick={() => handleDeleteActionItem(index)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Delete action item"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Add New Action Item */}
                {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Add new action item..."
                        className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleAddActionItem(e.target.value);
                                e.target.value = '';
                            }
                        }}
                    />
                    <button
                        onClick={(e) => {
                            const input = e.target.parentElement.querySelector('input');
                            handleAddActionItem(input.value);
                            input.value = '';
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                        Add
                    </button>
                </div>
                )}
            </div>

            {/* Action Item Note Dialog */}
            {showActionItemNoteDialog && pendingActionItemIndex !== null && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Action Item Completed</h3>
                                <p className="text-sm text-gray-600">Add a note about this completion?</p>
                            </div>
                        </div>

                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700">
                                "{actionItems[pendingActionItemIndex]?.text}"
                            </p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Note (Optional)
                            </label>
                            <textarea
                                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                rows={3}
                                placeholder="Add details about completing this action item..."
                                value={actionItemNote}
                                onChange={(e) => setActionItemNote(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => handleSaveActionItemCompletion(true)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                            >
                                Skip Note
                            </button>
                            <button
                                onClick={() => handleSaveActionItemCompletion(false)}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors"
                            >
                                {actionItemNote.trim() ? 'Save with Note' : 'Complete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Completion Dialog */}
            {showCompletionDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">All Action Items Complete!</h3>
                                <p className="text-sm text-gray-600">Would you like to mark this task as completed?</p>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Completion Note <span className="text-red-600">*</span>
                            </label>
                            <textarea
                                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                rows={4}
                                placeholder="Enter completion notes (required)..."
                                value={completionNote}
                                onChange={(e) => setCompletionNote(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowCompletionDialog(false);
                                    setCompletionNote('');
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCompleteTask}
                                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                                disabled={saving || !completionNote.trim()}
                            >
                                {saving ? 'Completing...' : 'Complete Task'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 lg:p-10">
                <div className="flex border-b mb-6">
                    <button
                        className={`px-6 py-3 -mb-px font-medium transition-colors ${activeTab === 'notes' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
                        onClick={() => setActiveTab('notes')}
                    >
                        Notes ({notesArr.length})
                    </button>
                    <button
                        className={`px-6 py-3 -mb-px font-medium transition-colors ${activeTab === 'attachments' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
                        onClick={() => setActiveTab('attachments')}
                    >
                        Attachments ({attachments.length})
                    </button>
                    <button
                        className={`px-6 py-3 -mb-px font-medium transition-colors ${activeTab === 'activity' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'}`}
                        onClick={() => setActiveTab('activity')}
                    >
                        Activity Log
                    </button>
                </div>

                {activeTab === 'notes' && (
                    <div>
                        {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                        <div className="mb-4">
                            <textarea
                                className="border p-2 w-full rounded mb-2"
                                rows={3}
                                placeholder="Add a note..."
                                value={note}
                                onChange={e => setNote(e.target.value)}
                            />
                            <div className="flex items-center space-x-2">
                                <button
                                    className="bg-indigo-600 text-white px-4 py-2 rounded"
                                    onClick={saveNote}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Note'}
                                </button>
                            </div>
                        </div>
                        )}

                        <div>
                            <input
                                type="text"
                                className="mb-3 border rounded px-2 py-1 w-full"
                                placeholder="Search notes..."
                                value={notesSearch}
                                onChange={e => setNotesSearch(e.target.value)}
                            />
                            <div className="mb-4 w-full border rounded p-3 bg-gray-50 break-words">
                                {notesArr.length === 0 && (
                                    <div className="text-gray-400 text-sm">No notes yet.</div>
                                )}
                                {notesArr
                                    .filter(n =>
                                        n.text?.toLowerCase().includes(notesSearch.toLowerCase()) ||
                                        n.user?.toLowerCase().includes(notesSearch.toLowerCase())
                                    )
                                    .map((n, idx) => {
                                        // Check if this note is associated with an action item
                                        const isActionItemNote = n.text?.includes('✓ Action Item Completed:') || n.text?.includes('✅ Task Completed:');
                                        
                                        return (
                                            <div key={n.id || idx} className={`mb-3 pb-2 break-words ${idx < notesArr.filter(note => note.text?.toLowerCase().includes(notesSearch.toLowerCase()) || note.user?.toLowerCase().includes(notesSearch.toLowerCase())).length - 1 ? 'border-b' : ''}`}>
                                                {isActionItemNote ? (
                                                    <div className="flex items-start gap-2 bg-white rounded p-2 border-l-4 border-indigo-500">
                                                        <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span className="break-words break-all whitespace-pre-wrap flex-1">{n.text}</span>
                                                    </div>
                                                ) : (
                                                    <span className="break-words break-all whitespace-pre-wrap">{n.text}</span>
                                                )}
                                                <span className="block text-xs text-gray-400 mt-1">
                                                    {n.user && (
                                                        <span className="text-indigo-700 font-semibold">{n.user}</span>
                                                    )}
                                                    {n.user && n.date ? ' • ' : ''}
                                                    {n.date ? new Date(n.date).toLocaleString() : ''}
                                                </span>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'attachments' && (
                    <div className="bg-white rounded-lg shadow p-4 mt-6">
                        <h3 className="text-lg font-semibold mb-4">Task Attachments</h3>
                        <div className="mb-4">
                            {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                            <label className="px-4 py-2 bg-indigo-600 text-white rounded cursor-pointer hover:bg-indigo-500">
                                {uploading ? 'Uploading...' : 'Upload File'}
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.xlsx,.csv"
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                    disabled={uploading}
                                    ref={fileInputRef}
                                />
                            </label>
                            )}
                            {uploadError && <span className="ml-4 text-red-600">{uploadError}</span>}
                        </div>
                        <ul className="divide-y">
                            {attachments.length === 0 ? (
                                <li className="py-4 text-gray-500">No attachments uploaded.</li>
                            ) : (
                                attachments.map(att => {
                                    // Find the user who uploaded the file
                                    const uploader =
                                        users.find(u => u.uid === att.userId || u.email === att.userEmail);

                                    // Get display name or fallback to email/uid
                                    const uploaderName =
                                        uploader
                                            ? (uploader.firstName || uploader.lastName
                                                ? `${uploader.firstName || ''} ${uploader.lastName || ''}`.trim()
                                                : uploader.displayName || uploader.name || uploader.email || uploader.uid)
                                            : att.userEmail || att.userId || 'Unknown';

                                    const uploadedDate =
                                        att.uploadedAt && att.uploadedAt.toDate
                                            ? att.uploadedAt.toDate().toLocaleString()
                                            : '';

                                    return (
                                        <li key={att.id} className="py-2 flex items-center justify-between">
                                            <div>
                                                <a
                                                    href={att.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    {att.name}
                                                </a>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <span className="text-xs text-gray-400">
                                                    Uploaded: <span className="font-semibold">{uploaderName}</span>
                                                    {uploadedDate && ` - ${uploadedDate}`}
                                                </span>
                                                {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                                                <button
                                                    className="ml-4 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 text-xs"
                                                    onClick={() => handleDeleteAttachment(att)}
                                                >
                                                    Delete
                                                </button>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })
                            )}
                        </ul>
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div className="overflow-x-auto">
        <div className="flex items-center mb-3">
            <span className="mr-2 font-medium text-gray-700">Search:</span>
            <input
                type="text"
                className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                    activitySearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                }`}
                placeholder="Search activity log..."
                value={activitySearch}
                onChange={e => setActivitySearch(e.target.value)}
            />
        </div>
        <table className="w-full whitespace-no-wrap">
            <thead>
                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Details</th>
                </tr>
            </thead>
            <tbody>
                {logs.length === 0 ? (
                    <tr>
                        <td colSpan={5} className="text-center py-4 text-gray-500">No activity found.</td>
                    </tr>
                ) : (
                    (activitySearch
                        ? logs.filter(log => {
                            const hay = [
                                log.action,
                                log.details,
                                log.userName,
                                log.userEmail,
                                log.userId
                            ].filter(Boolean).join(' ').toLowerCase();
                            return hay.includes(activitySearch.trim().toLowerCase());
                        })
                        : logs
                    ).map(log => {
                        const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                        // Find user by userId or userEmail
                        const user =
                            users.find(
                                u =>
                                    (log.userId && (u.uid === log.userId || u.id === log.userId)) ||
                                    (log.userEmail && u.email === log.userEmail)
                            );
                        const displayName =
                            user?.displayName ||
                            user?.name ||
                            `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                            log.userName ||
                            log.userEmail ||
                            log.userId ||
                            '-';
                        return (
                            <tr key={log.id} className="text-gray-700 hover:bg-gray-50">
                                <td className="px-4 py-3">{dateObj.toLocaleDateString()}</td>
                                <td className="px-4 py-3">{dateObj.toLocaleTimeString()}</td>
                                <td className="px-4 py-3">{displayName}</td>
                                <td className="px-4 py-3">{log.action || '-'}</td>
                                <td className="px-4 py-3">{log.details || '-'}</td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </table>
    </div>
)}
            </div>

            {/* TaskModal for editing */}
            {showEditModal && (
                <TaskModal
                    initial={task}
                    salesReps={users}
                    isAdmin={canViewAll}
                    customers={customers}
                    opportunities={opportunities}
                    orders={orders}
                    contacts={contacts}
                    onLoadCustomers={loadCustomers}
                    onLoadOpportunities={loadOpportunities}
                    onLoadContacts={loadContacts}
                    onClose={() => setShowEditModal(false)}
                    onSave={async (taskData) => {
                        try {
                            // Convert dueDate to Timestamp if needed
                            let dueDateTimestamp = taskData.dueDate;
                            if (taskData.dueDate) {
                                if (taskData.dueDate instanceof Date) {
                                    dueDateTimestamp = Timestamp.fromDate(taskData.dueDate);
                                } else if (typeof taskData.dueDate === 'string') {
                                    const date = new Date(taskData.dueDate + 'T00:00:00');
                                    dueDateTimestamp = Timestamp.fromDate(date);
                                } else if (!taskData.dueDate?.toDate) {
                                    dueDateTimestamp = Timestamp.fromDate(new Date(taskData.dueDate));
                                }
                            }

                            // Resolve associations
                            const resolvedAssociations = await resolveAssociations({
                                customerId: taskData.customerId || null,
                                opportunityId: taskData.opportunityId || null,
                                orderId: taskData.orderId || null,
                                contactId: taskData.contactId || null
                            });

                            // Prepare update data
                            const updateData = {
                                title: taskData.title,
                                description: taskData.description || '',
                                dueDate: dueDateTimestamp,
                                uid: taskData.uid,
                                assignedTo: taskData.assignedTo || '',
                                status: (taskData.status || 'open').toLowerCase(),
                                customerId: taskData.customerId || null,
                                opportunityId: taskData.opportunityId || null,
                                orderId: taskData.orderId || null,
                                contactId: taskData.contactId || null,
                                associations: resolvedAssociations,
                                actionItems: taskData.actionItems || [],
                                updatedAt: serverTimestamp()
                            };

                            // Capture old for logging
                            const old = task || {};
                            await updateDoc(doc(db, collectionPath('tasks', id)), updateData);

                            // Update local action items state
                            setActionItems(taskData.actionItems || []);

                            // Log field-level changes
                            try {
                                await logEntityChanges({
                                    oldObj: old,
                                    newObj: { ...old, ...taskData },
                                    fields: ['title', 'description', 'dueDate', 'uid', 'status', 'customerId', 'opportunityId', 'orderId', 'contactId'],
                                    entityType: 'Task',
                                    entityId: id,
                                    user: {
                                        uid: currentUser?.uid,
                                        email: currentUser?.email,
                                        displayName: currentUser?.displayName,
                                        firstName: currentUser?.firstName,
                                        lastName: currentUser?.lastName,
                                        id: currentUser?.id,
                                        collectionName: collectionPath('logsTask')
                                    },
                                    action: 'Updated Task',
                                    users
                                });
                            } catch (logErr) {
                                console.error('Failed to log task changes', logErr);
                            }

                            setShowEditModal(false);
                        } catch (err) {
                            console.error('Failed to save task edit', err);
                            alert('Failed to save changes: ' + err.message);
                        }
                    }}
                />
            )}
        </main>
    );
};

export default TaskDetailPage;
